/**
 * Task card component for the task list.
 * Shows SO number (NOT customer name), status, estimate, and labor type.
 */

import type { ContractorTask } from '../lib/api';
import { StatusBadge } from './StatusBadge';
import { TimerCompact } from './Timer';
import {
  formatEstimateRange,
  formatScheduledRange,
  getLaborBucketLabel,
} from '../lib/format';

interface TaskCardProps {
  task: ContractorTask;
  onClick?: () => void;
}

// Status indicator colors for the left strip
const statusStripColors: Record<string, string> = {
  pending: 'bg-status-pending',
  ready: 'bg-status-ready',
  staged: 'bg-status-staged',
  partial_staged: 'bg-amber-500',
  in_progress: 'bg-status-in-progress',
  complete: 'bg-status-complete',
  cancelled: 'bg-status-cancelled',
  abandoned: 'bg-status-cancelled',
};

export function TaskCard({ task, onClick }: TaskCardProps) {
  const isActionable = task.status === 'ready' || task.status === 'in_progress';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        task-card w-full text-left cursor-pointer
        ${isActionable ? 'border-accent/30' : ''}
        focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface
      `}
    >
      {/* Status strip */}
      <div
        className={`status-strip ${statusStripColors[task.status] ?? 'bg-status-pending'}`}
      />

      <div className="pl-4 pr-3 py-3">
        {/* Top row: SO number + Status */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="min-w-0">
            {/* SO Number - monospace, prominent */}
            <div className="font-mono text-sm font-semibold text-ink truncate">
              {task.gpSalesOrderNumber}
            </div>
            {/* Part number */}
            <div className="font-mono text-xs text-ink-muted truncate mt-0.5">
              {task.partNumber}
            </div>
          </div>
          <StatusBadge status={task.status} />
        </div>

        {/* Part description - truncated */}
        <p className="text-sm text-ink-muted truncate-2 mb-3">
          {task.partDescription}
        </p>

        {/* Bottom row: Labor bucket + Estimate */}
        <div className="flex items-center justify-between gap-2 text-xs">
          <div className="flex items-center gap-1.5">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-surface-highlight">
              <WorkIcon className="w-3 h-3 text-ink-subtle" />
            </span>
            <span className="text-ink-subtle font-medium">
              {getLaborBucketLabel(task.laborBucket)}
            </span>
          </div>

          <div className="flex items-center gap-1.5 text-ink-subtle">
            <ClockIcon className="w-3.5 h-3.5" />
            <span className="font-mono">
              {formatEstimateRange(
                task.estimatedMinutesMin,
                task.estimatedMinutesMax
              )}
            </span>
          </div>
        </div>

        {/* Active timer - when in progress */}
        {task.status === 'in_progress' && task.activeTimeEntry && (
          <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-status-in-progress/30 text-xs">
            <div className="w-2 h-2 rounded-full bg-status-in-progress animate-pulse" />
            <span className="text-status-in-progress font-medium">Working</span>
            <span className="text-ink-subtle">·</span>
            <TimerCompact startTime={task.activeTimeEntry.startTime} />
          </div>
        )}

        {/* Scheduled time - if available and not in progress */}
        {task.scheduledStart && task.status !== 'in_progress' && (
          <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-rule text-xs text-ink-subtle">
            <CalendarIcon className="w-3.5 h-3.5" />
            <span>
              {formatScheduledRange(task.scheduledStart, task.scheduledEnd)}
            </span>
          </div>
        )}
      </div>
    </button>
  );
}

// Simple inline icons
function WorkIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2 5h12v8a1 1 0 01-1 1H3a1 1 0 01-1-1V5z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5 5V3a1 1 0 011-1h4a1 1 0 011 1v2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        cx="8"
        cy="8"
        r="6"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M8 4.5V8l2.5 1.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="2"
        y="3"
        width="12"
        height="11"
        rx="1"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M2 6h12M5 2v2M11 2v2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
